import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type {
  FlourSplitRow,
  PlantBalanceRow,
  PlantTable,
  ProductionReport,
  ProductionUnitQuantity,
} from "../../api/reports";
import type { ChartPalette } from "../../theme/useChartPalette";
import { formatNumber } from "../../lib/format";
import { StabloRegistar } from "../../components/reports/StabloRegistar";
import type { StabloRed, StabloSort } from "../../components/reports/StabloRegistar";
import { th } from "./stil";

/**
 * Proizvodnja: šta je svaki pogon potrošio i šta je napravio, sa spuštanjem po smeru do grupe.
 *
 * Sedam jedinica, i oblik im diktira štampani izveštaj a ne baza: pogon je naslov, pod njim jedna
 * ili više tabela grupa/količina/vrednost, a pored njega nekoliko samostalnih brojeva (proizvedeno,
 * prodato, utrošeno drugde).
 *
 * Četiri stvari ekran mora da kaže:
 *  - **Smerovi se ne sabiraju.** Pogonska brojka je PROIZVEDENO i dolazi iz zapisa
 *    (`producedQuantity`), a ne iz zbira tabela. Dok je ekran sabirao utrošeno + pomoćne +
 *    proizvedeno, MCC1 je stajao na 9.160.267 kg umesto 4.394.700 kg; utrošak stoji POred, kao
 *    dodatak, nikad unutra.
 *  - Podela sojinog brašna na staru i novu fabriku **ne pokriva celu grupu**, pa ostatak ima svoj
 *    red umesto da ostane rupa u računu.
 *  - Gde štampani izveštaj u koloni vrednosti nosi **zbir stare šifre artikla** a ne vrednost, to
 *    se imenuje i pored stoji prava vrednost. Tiha ispravka bi ostavila čitaoca bez načina da
 *    uporedi našu stranu sa onom koju godinama čita.
 *  - **Spuštanje staje na grupi.** Uvezeni proizvodni nalozi ne nose šifru ni ime artikla — samo
 *    grupu i nadgrupu — pa nivoa artikla nema. To piše nad tabelom, jer tabela koja se ne otvara
 *    dalje izgleda isto kao tabela kojoj je nešto otkazalo.
 */

const dec2 = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/** Oznaka smera se prevodi NA EKRANU; server šalje CONSUMED, AUXILIARY ili PRODUCED. */
function imeSmera(smer: PlantTable["direction"], t: (k: string, d: string) => string): string {
  if (smer === "CONSUMED") return t("report.prod.direction.CONSUMED", "Utrošeno");
  if (smer === "AUXILIARY") return t("report.prod.direction.AUXILIARY", "Pomoćne sirovine");
  return t("report.prod.direction.PRODUCED", "Proizvedeno");
}

export default function ProizvodnjaTab({
  podaci,
  locale,
  palette,
  ucitava,
}: {
  podaci: ProductionReport;
  locale: string;
  palette: ChartPalette;
  ucitava: boolean;
}) {
  const { t } = useTranslation();
  const [otvoreni, setOtvoreni] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<StabloSort>(null);
  const [opadajuce, setOpadajuce] = useState(true);

  /**
   * Pogoni po PROIZVEDENOJ količini — slika koja kaže ko nosi mesec.
   *
   * Mera dolazi iz odgovora, ne iz zbira tabela: utrošeno i proizvedeno su dva smera i njihov
   * zbir nije nijedna veličina. Ista brojka ide i u PDF.
   */
  const poPogonu = useMemo(
    () =>
      podaci.plants
        .map((p) => ({ ime: p.name, kolicina: p.producedQuantity ?? 0 }))
        .filter((p) => p.kolicina > 0)
        .sort((a, b) => b.kolicina - a.kolicina),
    [podaci.plants],
  );

  const otvori = useCallback((kljuc: string) => {
    setOtvoreni((pre) => {
      const sled = new Set(pre);
      if (sled.has(kljuc)) sled.delete(kljuc);
      else sled.add(kljuc);
      return sled;
    });
  }, []);

  const sortiraj = useCallback(
    (k: StabloSort) => {
      if (k === sort) setOpadajuce((o) => !o);
      else {
        setSort(k);
        setOpadajuce(k !== "ime");
      }
    },
    [sort],
  );

  /**
   * Količine van kilograma — imenovane, nikad pribrojene tonaži.
   *
   * Prazna oznaka jedinice NIJE kilogram nego nepoznata, pa se i tako imenuje: red koji bi tu pao
   * na „kg" ušao bi u tonažu, a to je kvar zbog kog kolona jedinice i postoji.
   */
  const vanKilograma = useCallback(
    (druge: ProductionUnitQuantity[] | undefined): string | undefined => {
      const sa = (druge ?? []).filter((u) => u.quantity !== 0);
      if (sa.length === 0) return undefined;
      return sa
        .map(
          (u) =>
            `+ ${formatNumber(u.quantity, locale, dec2)} ` +
            (u.unit?.trim()
              ? t(`report.prod.unit.${u.unit.toLowerCase()}`, u.unit.toLowerCase())
              : t("report.prod.unit.unknown", "jedinica nepoznata")),
        )
        .join(" · ");
    },
    [locale, t],
  );

  const stablo: StabloRed[] = useMemo(() => {
    const redovi = podaci.plants.map((p) => {
      const utroseno = p.figures.find((f) => f.kind === "CONSUMED")?.value ?? 0;
      const vrednostIzlaza = p.tables
        .filter((tab) => tab.direction === "PRODUCED")
        .reduce((s, tab) => s + (tab.totalValue ?? 0), 0);
      return {
        kljuc: p.code,
        ime: p.name,
        // Pogon koji štampani izveštaj ne pokazuje nosi oznaku, na jeziku ekrana.
        oznaka: p.inPrintedReport ? undefined : t("report.prod.notInPrintedShort", "nije u štampanom"),
        oznakaTon: "paznja" as const,
        mere: [
          { sada: p.producedQuantity, jedinica: "kg", decimala: 2 },
          { sada: vrednostIzlaza === 0 ? null : vrednostIzlaza, decimala: 2 },
        ],
        // Utrošak stoji POred izlaza, imenovan — nikad pribrojen. Isti razlog kao komadi uz tonažu.
        dodatak:
          [
            utroseno === 0
              ? undefined
              : t("report.prod.consumedBeside", {
                  iznos: formatNumber(utroseno, locale, dec2),
                  defaultValue: "utrošeno {{iznos}} kg",
                }),
            vanKilograma(p.producedOtherUnits),
          ]
            .filter(Boolean)
            .join(" · ") || undefined,
        mozeDaSeOtvori: p.tables.length > 0,
        prazno: t("report.prod.noTables", "Za ovaj pogon nema naloga u periodu."),
        deca: p.tables.map((tab) => {
          const bezVrednosti = tab.lines.every((l) => l.value == null);
          return {
            kljuc: `${p.code}\u0000${tab.direction}`,
            ime: imeSmera(tab.direction, t),
            mere: [
              { sada: tab.totalQuantity, jedinica: "kg", decimala: 2 },
              bezVrednosti ? { sada: null, vanNivoa: true } : { sada: tab.totalValue, decimala: 2 },
            ],
            dodatak: vanKilograma(tab.otherUnits),
            mozeDaSeOtvori: tab.lines.length > 0,
            deca: tab.lines.map((l) => ({
              kljuc: `${p.code}\u0000${tab.direction}\u0000${l.group || "__nerazvrstano"}`,
              ime: l.group || t("report.prod.unclassified", "nerazvrstano"),
              mere: [
                { sada: l.quantity, jedinica: "kg", decimala: 2 },
                l.value == null ? { sada: null, vanNivoa: true } : { sada: l.value, decimala: 2 },
              ],
              dodatak: vanKilograma(l.otherUnits),
            })),
          };
        }),
      };
    });
    if (sort === null) return redovi;
    const znak = opadajuce ? -1 : 1;
    if (sort === "ime") return [...redovi].sort((a, b) => znak * a.ime.localeCompare(b.ime));
    const mera = (r: StabloRed) => (r.mere[sort as number]?.sada as number) ?? 0;
    return [...redovi].sort((a, b) => znak * (mera(a) - mera(b)));
  }, [locale, opadajuce, podaci.plants, sort, t, vanKilograma]);

  /** Tabele u kojima štampana kolona vrednosti nije vrednost; imenuju se zajedno, ispod registra. */
  const stareSifre = useMemo(
    () =>
      podaci.plants.flatMap((p) =>
        p.tables
          .filter((tab) => tab.printedCodeSum != null)
          .map((tab) => ({ pogon: p.name, smer: tab.direction, iznos: tab.printedCodeSum as number })),
      ),
    [podaci.plants],
  );

  const nemaNista = podaci.plants.length === 0 && podaci.flourSplit.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: ucitava ? 0.55 : 1 }}>
      {podaci.notMeasured.length > 0 && (
        /* Ono što još nije reprodukovano iz ERP-a se IMENUJE. Prazan odeljak bez reči čita se kao
           „nije bilo proizvodnje", što je druga tvrdnja i nije tačna. */
        <div
          className="card"
          style={{ padding: "10px 14px", fontSize: 13, color: "var(--ink-2)", borderLeft: "3px solid var(--warn)" }}
        >
          {t("report.prod.notMeasured", {
            spisak: podaci.notMeasured.join(", "),
            defaultValue: "Još nije reprodukovano iz ERP-a: {{spisak}}.",
          })}
        </div>
      )}

      {nemaNista && podaci.notMeasured.length === 0 && (
        <div className="empty" style={{ padding: 14 }}>
          {t("report.prod.empty", "Za ovaj period nema proizvodnih naloga.")}
        </div>
      )}

      {poPogonu.length > 1 && (
        <section className="card" style={{ padding: 14 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
            {t("report.prod.chart", "Pogoni po proizvedenoj količini")}
          </h3>
          <div className="sub" style={{ marginBottom: 10 }}>
            {t(
              "report.prod.chartHint",
              "Mera je proizvedeno, ne zbir smerova: utrošak i izlaz su dve veličine i njihov zbir nije nijedna.",
            )}
          </div>
          <ResponsiveContainer width="100%" height={Math.max(220, poPogonu.length * 34)}>
            <BarChart data={poPogonu} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: palette.ink }}
                tickFormatter={(v: number) => formatNumber(v, locale, { maximumFractionDigits: 0 })}
              />
              <YAxis type="category" dataKey="ime" width={150} tick={{ fontSize: 11, fill: palette.ink }} />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
                labelStyle={{ color: palette.inkStrong }}
                formatter={(v) => formatNumber(Number(v), locale, dec2)}
              />
              <Bar dataKey="kolicina" name={t("report.prod.quantityKg", "Količina, kg")} radius={[0, 3, 3, 0]}>
                {poPogonu.map((p) => (
                  <Cell key={p.ime} fill={palette.brand} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {podaci.plantBalance.length > 0 && (
        <BilansPogona
          redovi={podaci.plantBalance}
          stara={podaci.oldPlantByGroup}
          bezPogona={podaci.soldWithoutPlant}
          zemlja={podaci.country}
          locale={locale}
        />
      )}
      {podaci.flourSplit.length > 0 && (
        <BrasnoPoFabrici
          redovi={podaci.flourSplit}
          ostatak={podaci.flourRemainder}
          zemlja={podaci.country}
          locale={locale}
        />
      )}

      {podaci.plants.length > 0 && (
        <section className="card" style={{ padding: 0 }}>
          <h3 style={{ margin: 0, padding: "14px 14px 0", fontSize: 15 }}>
            {t("report.prod.byPlant", "Pogoni, po smeru i grupi")}
          </h3>
          <div className="sub" style={{ padding: "6px 14px 10px" }}>
            {t(
              "report.prod.drillLimit",
              "Red pogona nosi proizvedenu količinu; utrošak stoji pored nje, ne u njoj. Spuštanje staje na grupi — uvezeni nalozi ne nose šifru artikla. Prošle godine ovaj deo ne nosi, pa kolona promene stoji prazna.",
            )}
          </div>
          <StabloRegistar
            kolone={[
              t("report.prod.plantHead", "Pogon"),
              t("report.prod.quantityKg", "Količina, kg"),
              t("report.prod.value", "Vrednost"),
            ]}
            sort={sort}
            opadajuce={opadajuce}
            naSortiranje={sortiraj}
            redovi={stablo}
            otvoreni={otvoreni}
            naOtvaranje={otvori}
            locale={locale}
          />
        </section>
      )}

      {stareSifre.length > 0 && (
        /* Kolona u registru nosi PRAVU vrednost, pa se sabira. Ovde se imenuje brojka koja stoji u
           štampanom izveštaju — ona nije vrednost nego zbir stare šifre artikla. Tiha ispravka bi
           onemogućila poređenje sa stranom koja se godinama čita. */
        <div className="card" style={{ padding: "10px 14px", borderLeft: "3px solid var(--warn)" }}>
          <div style={th}>{t("report.prod.printedDiffers", "Gde se štampani izveštaj razlikuje")}</div>
          {stareSifre.map((x) => (
            <div key={`${x.pogon}-${x.smer}`} className="sub" style={{ marginTop: 4 }}>
              {t("report.prod.articleCodeNote", {
                pogon: x.pogon,
                smer: imeSmera(x.smer as PlantTable["direction"], t),
                iznos: formatNumber(x.iznos, locale, dec2),
                defaultValue:
                  "{{pogon}}, {{smer}}: kolona vrednosti ovde nosi pravu vrednost. U štampanom izveštaju ta kolona nije vrednost nego zbir stare šifre artikla, i glasi {{iznos}}.",
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Minimalni proizvodni bilans: proizvedeno prema izašlom.
 *
 * Blok koji štampani original nosi a portal nije imao. Dve tabele, jer su dva pitanja: prva kaže
 * koliko je svaki pogon napravio i koliko je od toga prodato, druga razlaže staru fabriku po grupi
 * i uz prodato nosi ono što je otišlo u drugi pogon.
 *
 * **Proizvodnja se NE umanjuje** za ono što je ušlo u drugi proizvod: ta količina ostaje na svom
 * pogonu i pojavljuje se kao utrošak drugog — tako prodajna statistika ostaje samo prodaja.
 */
function BilansPogona({
  redovi,
  stara,
  bezPogona,
  zemlja,
  locale,
}: {
  redovi: PlantBalanceRow[];
  stara: PlantBalanceRow[];
  bezPogona: number;
  zemlja: string | null;
  locale: string;
}) {
  const { t } = useTranslation();
  if (redovi.length === 0) return null;
  return (
    <section className="card" style={{ padding: 0, overflowX: "auto" }}>
      <h3 style={{ margin: 0, padding: "14px 14px 0", fontSize: 15 }}>
        {t("report.prod.plantBalance", "Proizvodni bilans po pogonu")}
      </h3>
      <div className="sub" style={{ padding: "6px 14px 0" }}>
        {t(
          "report.prod.plantBalanceHint",
          "Dve kolone nisu ista populacija: prodato se spaja po ARTIKLU, ne po lotu, pa sadrži i kupljene lotove tih istih artikala — zato odnos ume i preko 100 %. Proizvodnja se NE umanjuje za ono što je otišlo u drugi proizvod: ta količina ostaje na svom pogonu i pojavljuje se kao utrošak drugog pogona.",
        )}
      </div>
      {/* JEDAN RED, DVE MERE RAZLIČITOG OBUHVATA. „Prodato" nosi zemlju sa koje je izveštaj
          otvoren, „proizvedeno" je ne poznaje. Bez ove rečenice se odnos čita kao da su obe iz
          istog skupa — a to su prodato u jednoj zemlji prema proizvedenom svuda. */}
      {zemlja && (
        <div className="sub" style={{ padding: "6px 14px 0" }}>
          {t("report.prod.soldScope", {
            defaultValue:
              "Kolona „prodato“ obuhvata samo {{country}}. „Proizvedeno“ i utrošak su ukupni: proizvodnja nema zemlju — nju ima kupac, ne nalog.",
            country: zemlja,
          })}
        </div>
      )}
      <table className="tbl">
        <thead>
          <tr>
            <th>{t("report.prod.plant", "Fabrika")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.produced", "Proizvedeno")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.sold", "Prodato")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.soldShare", "Prodato / proizvedeno")}</th>
          </tr>
        </thead>
        <tbody>
          {redovi.map((b) => (
            <tr key={b.plant}>
              <td>{b.plantName || b.plant}</td>
              <td style={{ textAlign: "right" }}>{kg(b.produced, locale)}</td>
              <td style={{ textAlign: "right" }}>{kg(b.sold, locale)}</td>
              {/* Udeo bez proizvodnje nije nula nego crta: deljenje nulom nije nula posto. */}
              <td style={{ textAlign: "right" }}>
                {b.produced > 0
                  ? `${formatNumber((b.sold / b.produced) * 100, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
                  : "/"}
              </td>
            </tr>
          ))}
          {/* Ono što nijedan pogon ne može da prisvoji stoji kao svoj red, ne izostavlja se. */}
          <tr>
            <td style={{ fontWeight: 600 }}>{t("report.prod.withoutPlant", "Bez pogona")}</td>
            <td style={{ textAlign: "right" }}>/</td>
            <td style={{ textAlign: "right" }}>{kg(bezPogona, locale)}</td>
            <td style={{ textAlign: "right" }}>/</td>
          </tr>
        </tbody>
      </table>
      <div className="sub" style={{ padding: "6px 14px 14px" }}>
        {t(
          "report.prod.withoutPlantHint",
          "Bez pogona je roba koju Bankom prodaje a ne pravi, i artikli koje mapa artikal→pogon ne pokriva. Imenuje se, a ne izostavlja.",
        )}
      </div>

      {stara.length > 0 && (
        <>
          <h3 style={{ margin: 0, padding: "0 14px", fontSize: 14 }}>
            {stara[0].plantName || stara[0].plant}
          </h3>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("report.col.group", "Grupa")}</th>
                <th style={{ textAlign: "right" }}>{t("report.prod.produced", "Proizvedeno")}</th>
                <th style={{ textAlign: "right" }}>{t("report.prod.sold", "Prodato")}</th>
                <th style={{ textAlign: "right" }}>
                  {t("report.prod.consumedElsewhere", "Utrošak u drugom pogonu")}
                </th>
                <th style={{ textAlign: "right" }}>{t("report.prod.totalOut", "Ukupan izlaz")}</th>
              </tr>
            </thead>
            <tbody>
              {stara.map((b) => (
                <tr key={b.articleGroup || "-"}>
                  <td>{b.articleGroup || t("report.prod.unclassified", "nerazvrstano")}</td>
                  <td style={{ textAlign: "right" }}>{kg(b.produced, locale)}</td>
                  <td style={{ textAlign: "right" }}>{kg(b.sold, locale)}</td>
                  {/* Crta, ne nula: nula bi tvrdila da u drugom pogonu nije utrošeno ništa. */}
                  <td style={{ textAlign: "right" }}>
                    {b.consumedElsewhere == null ? "/" : kg(b.consumedElsewhere, locale)}
                  </td>
                  <td style={{ textAlign: "right" }}>{kg(b.totalOut, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sub" style={{ padding: "6px 14px 14px" }}>
            {t(
              "report.prod.consumedElsewhereHint",
              "Utrošak je prazan gde se ne može pripisati: proizvodnja nosi grupu a ne artikal, pa kad istu grupu prave dva pogona utrošak u trećem ne može da se podeli.",
            )}
          </div>
        </>
      )}
    </section>
  );
}

/** Kilogrami bez decimala: tonaža se čita, ne meri na gram. */
function kg(v: number, locale: string): string {
  return formatNumber(v, locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function BrasnoPoFabrici({
  redovi,
  ostatak,
  zemlja,
  locale,
}: {
  redovi: FlourSplitRow[];
  ostatak: FlourSplitRow | null;
  /** Zemlja za koju je podela merena; tabele pogona iznad je ne poznaju, pa se ovde ispisuje. */
  zemlja: string | null;
  locale: string;
}) {
  const { t } = useTranslation();
  const svi = ostatak ? [...redovi, ostatak] : redovi;
  return (
    <section className="card" style={{ padding: 0, overflowX: "auto" }}>
      <h3 style={{ margin: 0, padding: "14px 14px 0", fontSize: 15 }}>
        {t("report.prod.flourSplit", "Prodato sojino brašno, po fabrici koja ga je napravila")}
      </h3>
      {/* Obuhvat se IMENUJE kad se razlikuje od tabela iznad: podela je prodajna mera i nosi
          zemlju, pogoni su proizvodni i nemaju je. Bez toga su dva broja o istom brašnu na
          istoj strani izgledala kao jedan. */}
      {zemlja && (
        <div className="sub" style={{ padding: "6px 14px 0" }}>
          {t("report.prod.flourScope", {
            defaultValue:
              "Podela brašna obuhvata samo {{country}}. Proizvodne količine se ne filtriraju po zemlji: proizvodnja nema zemlju.",
            country: zemlja,
          })}
        </div>
      )}
      {ostatak && (
        <div className="sub" style={{ padding: "6px 14px 0" }}>
          {t(
            "report.prod.flourRemainderNote",
            "Dve fabrike ne pokrivaju celu grupu: ostatak stoji kao svoj red, umesto da ostane rupa u računu.",
          )}
        </div>
      )}
      <table className="tbl">
        <thead>
          <tr>
            <th>{t("report.prod.plant", "Fabrika")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.prevQuantity", "Količina prošle godine")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.quantity", "Količina")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.prevValue", "Vrednost prošle godine")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.value", "Vrednost")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.prevRuc", "RUC prošle godine")}</th>
            <th style={{ textAlign: "right" }}>{t("report.prod.ruc", "RUC")}</th>
          </tr>
        </thead>
        <tbody>
          {svi.map((f) => (
            <tr
              key={f.plant}
              style={{
                borderTop: "1px solid var(--border)",
                color: ostatak && f.plant === ostatak.plant ? "var(--ink-3)" : undefined,
              }}
            >
              <td>{f.plant}</td>
              <td className="num">{formatNumber(f.prevQuantity, locale, dec2)}</td>
              <td className="num" style={{ fontWeight: 600 }}>
                {formatNumber(f.quantity, locale, dec2)}
              </td>
              <td className="num">{formatNumber(f.prevSalesValue, locale, dec2)}</td>
              <td className="num">{formatNumber(f.salesValue, locale, dec2)}</td>
              <td className="num">{formatNumber(f.prevRuc, locale, dec2)}</td>
              <td className="num" style={{ color: f.ruc < 0 ? "var(--warn)" : undefined }}>
                {formatNumber(f.ruc, locale, dec2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
